import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Truck, Shield, Headphones, Leaf, Star, MessageCircle } from 'lucide-react';

const features = [
  { icon: Truck, title: 'Free Delivery', desc: 'On orders above Rs. 3,000' },
  { icon: Shield, title: 'DRAP Approved', desc: 'Certified & lab tested' },
  { icon: Headphones, title: '24/7 Support', desc: 'WhatsApp & Call' },
  { icon: Leaf, title: '100% Natural', desc: 'No chemicals or steroids' },
];

const stats = [
  { value: '60+', label: 'Years Experience' },
  { value: '1M+', label: 'Happy Customers' },
  { value: '100%', label: 'Natural Formula' },
  { value: '4.8★', label: 'Average Rating' },
];

export function Hero() {
  return (
    <div>
      {/* Main Hero Section */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-primary-dark">
        {/* Multi-layer background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-dark via-primary to-herbal-700" />
        <div className="absolute inset-0 hero-pattern" />
        {/* Decorative circles */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-primary-light/20 rounded-full blur-2xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — Text content */}
            <div className="animate-slide-up">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium mb-6">
                <Leaf className="w-4 h-4 text-accent" />
                Pakistan's #1 Herbal Care Brand
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6 tracking-tight">
                Nature's Power for{' '}
                <span className="relative inline-block">
                  <span className="text-accent">Better Health</span>
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 260 12" fill="none">
                    <path d="M2 10 C60 4, 180 4, 258 8" stroke="rgba(255,167,38,0.5)" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-lg leading-relaxed">
                Laboratory prepared, DRAP approved herbal formulations trusted by millions. 
                Premium quality, zero side effects, fast delivery across Pakistan.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/shop">
                  <Button variant="accent" size="lg" leftIcon={<Leaf className="w-5 h-5" />}>
                    Shop Now
                  </Button>
                </Link>
                <a
                  href="https://wa.me/923056367673"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex"
                >
                  <button
                    type="button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl font-bold text-base bg-[#25D366] hover:bg-[#1EBE5D] text-white shadow-xl shadow-black/20 hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer"
                  >
                    <MessageCircle className="w-5 h-5 fill-white text-white" />
                    <span>WhatsApp Consultation</span>
                  </button>
                </a>
              </div>

              {/* Inline stats */}
              <div className="grid grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl font-bold text-accent">{stat.value}</p>
                    <p className="text-xs text-white/60 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Logo/brand card */}
            <div className="hidden lg:flex justify-center items-center animate-fade-in">
              <div className="relative">
                <div className="absolute inset-0 bg-accent/20 rounded-3xl blur-3xl transform scale-110" />
                <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-10 text-center">
                  <img
                    src="/assets/Logo.jpg"
                    alt="WellZora Herbal Care"
                    className="w-40 h-auto mx-auto mb-6 rounded-2xl shadow-2xl"
                  />
                  <h2 className="text-2xl font-bold text-white mb-2">WellZora Herbal Care</h2>
                  <p className="text-white/70 text-sm mb-6">Pure • Natural • Trusted</p>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                    ))}
                  </div>
                  <p className="text-white/60 text-xs">Rated 4.8/5 by 1M+ customers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Row — separate from hero, no overlap */}
      <section className="relative bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-6 hover:bg-primary/5 transition-colors duration-300 group"
              >
                <div className="w-11 h-11 bg-primary/10 group-hover:bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{feature.title}</h3>
                  <p className="text-gray-500 text-xs mt-0.5">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}